import { Component, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd, RouterOutlet } from '@angular/router';
import { SubscriptionUtils } from '@yupma/ui-kit';
import { Subscription, filter } from 'rxjs';
import { RouteName } from './routing-path';
import { AppInitializerService, IAMService, OrganisationDataService, OrganisationMemberDataService, UIService, UserDataService } from './services/services.module';
import { Platform } from '@ionic/angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { Location } from '@angular/common';
import { FieldDocument, OrganisationDocument, OrganisationMemberDocument, UserDocument } from 'server-model';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  animations: [
    trigger('routeAnimations', [
      // Default animation for forward navigation
      transition('* => *', [
        style({ opacity: 0, transform: 'translateX(100%)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
      ]),
      // Animation for backward navigation
      transition('backwards => *', [
        style({ opacity: 0, transform: 'translateX(-100%)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
      ]),
    ]),
  ],
})
export class AppComponent implements OnDestroy {
  routeNames = RouteName;
  hideTopbar: boolean = false;
  hideNavigation: boolean = false;
  showTopbarUrls: RouteName[] = [RouteName.HOME];
  hideNavigationUrls: RouteName[] = [RouteName.AUTH, RouteName.LOGOUT, RouteName.ORGANISATIONS, RouteName.SETTINGS, RouteName.CONTACT, RouteName.INVITE, RouteName.WORKFLOW, RouteName.OAUTH, RouteName.HELP];
  private routerSubscription: Subscription = new Subscription;
  private organisationChangeSubscription: Subscription = new Subscription;
  private isBackNavigation = false;
  isLoading: boolean = false;
  userDocument: UserDocument | null = null;
  organisationDocument: OrganisationDocument | null = null;
  organisationMemberDocument: OrganisationMemberDocument | null = null;

  constructor(
    private platform: Platform,
    private router: Router,
    private location: Location,
    private appInitialzier: AppInitializerService,
    private iamService: IAMService,
    private uiService: UIService,
    private userDataService: UserDataService,
    private organisationDataService: OrganisationDataService,
    private organisationMemberDataService: OrganisationMemberDataService,
  ) {
    this.initializeApp();
  }

  private async initializeApp() {
    this.isLoading = true;
    await this.appInitialzier.init();
    this.subscribeRoute();
    this.subscribeObservables();
    this.platform.ready().then(async () => {
      const isAuthenticated = this.iamService.isAuthenticated();
      if (isAuthenticated) {
        await this.getUserDocument();
        await this.getOrganisationDocument();
        await this.getOrganisationMemeberDocument();
      }
      this.isLoading = false;
    })
    // Detect hardware back button press on mobile devices
    this.platform.backButton.subscribeWithPriority(10, () => {
      this.isBackNavigation = true;
    });

    // Detect browser back button (popstate event)
    this.location.subscribe(() => {
      this.isBackNavigation = true;
    });
  }

  ngOnDestroy() {
    SubscriptionUtils.unsubscribe(this.routerSubscription);
    SubscriptionUtils.unsubscribe(this.organisationChangeSubscription);
  }

  prepareRoute(outlet: RouterOutlet) {
    return this.isBackNavigation ? 'back' : 'forward';
  }

  private subscribeRoute() {
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navEndEvent = event as NavigationEnd;
        const urlSegmentsQuestion = navEndEvent.url.split('?');
        const urlSegments = urlSegmentsQuestion[0].split('/');
        this.hideNavigation = this.hideNavigationUrls.some(url => urlSegments.includes(url));
        this.hideTopbar = this.showTopbarUrls.some(url => urlSegments.includes(url));
        this.isBackNavigation = false;
        window.scrollTo(0, 0)
      });
  }

  private subscribeObservables() {
    this.organisationChangeSubscription = this.uiService.onOrganisationChangedEvent$.subscribe((value) => {
      this.getUserDocument();
      this.getOrganisationDocument();
      this.getOrganisationMemeberDocument();
    })
  }

  private async getUserDocument() {
    try {
      const uid = this.iamService.getUserId();
      this.userDocument = (await this.userDataService.getUser(uid)).data;
    } catch (error) {
      // if (this.iamService.getUserId() && !this.userDocument) {
      //   this.uiService.logout();
      // }
    }
  }

  private async getOrganisationDocument() {
    try {
      const organisationId = this.iamService.getOrganisationId();
      this.organisationDocument = (await this.organisationDataService.getOrganisation(organisationId)).data;
    } catch (error) {
      // if (this.iamService.getOrganisationId() && !this.organisationDocument && this.userDocument?.orgs.length) {
      //   this.uiService.logout();
      // }
    }
  }

  private async getOrganisationMemeberDocument() {
    try {
      const organisationId = this.iamService.getOrganisationId();
      const uid = this.iamService.getUserId();
      this.organisationMemberDocument = (await this.organisationMemberDataService.getOrganisationMember(organisationId, uid)).data;
    } catch (error) {
      if (this.iamService.getOrganisationId() && this.iamService.getUserId() && !this.organisationDocument && this.userDocument?.orgs.length) {
        this.uiService.logout();
      }
    }
  }
}
